import * as y from 'yup';
import {
  ApplicationContract,
  CoborrowerApplicationContract,
} from 'src/types/application';
import { FieldContract, ValidationFieldContract } from 'src/types/field';
import { LoanProductDataContract } from 'src/types/loanproductdata';
import { PropertyContract } from 'src/types/property';
import { PrequalificationContract } from 'src/types/prequalification';
import { getTypeValidationKeys } from 'src/util/typeValidationMessages';

const typeValidationKeys = getTypeValidationKeys();

export const LoanContract = y.object({
  id: y.string().uuid(),
  referralSource: y.string().optional(),
  expectedBorrowerCount: y.number().optional(),
  currentApplicationIndex: y.number().required(),
  purchasePriceAmount: y
    .number()
    .transform((value, originalValue) => {
      // Convert empty strings to undefined for proper optional handling
      return originalValue === '' ? undefined : value;
    })
    .optional()
    .test(
      'key:idealPurchasePrice',
      typeValidationKeys.loan.idealPurchasePrice,
      (val, ctx) => {
        // @ts-ignore
        const purpose = ctx.loan?.property?.loanPurposeType;
        if (purpose === 'PreApproval') {
          return (val || 0) > 0;
        }
        return true;
      },
    )
    .when('$loan', ([loan]) => {
      const purpose = loan?.property?.loanPurposeType;
      if (!purpose?.includes('Refinance') && purpose !== 'PreApproval') {
        return y
          .number()
          .transform((value, originalValue) => {
            return originalValue === '' ? undefined : value;
          })
          .required();
      }
      return y
        .number()
        .transform((value, originalValue) => {
          return originalValue === '' ? undefined : value;
        })
        .optional();
    }),
  proposedDuesAmount: y.number().optional(),
  proposedRealEstateTaxesAmount: y.number().optional(),
  proposedHazardInsuranceAmount: y.number().optional(),
  borrowerRequestedLoanAmount: y
    .number()
    .transform((value, originalValue) => {
      // Convert empty strings to undefined for proper optional handling
      return originalValue === '' ? undefined : value;
    })
    .optional()
    .when('$loan', ([loan]) => {
      if (loan?.property?.loanPurposeType?.includes('Refinance')) {
        return y
          .number()
          .transform((value, originalValue) => {
            return originalValue === '' ? undefined : value;
          })
          .required()
          .positive();
      }
      return y
        .number()
        .transform((value, originalValue) => {
          return originalValue === '' ? undefined : value;
        })
        .optional();
    }),
  downPayment: y.object({
    amount: y
      .number()
      .transform((value, originalValue) => {
        // Convert empty strings to undefined for proper optional handling
        return originalValue === '' ? undefined : value;
      })
      .optional(),
    sourceDescription: y
      .string()
      .transform((value, originalValue) => {
        // Convert empty strings to undefined for proper optional handling
        return originalValue === '' ? undefined : value;
      })
      .optional()
      .when('$loan', ([loan]) => {
        if (!loan?.property?.loanPurposeType?.includes('Refinance')) {
          return y
            .string()
            .transform((value, originalValue) => {
              return originalValue === '' ? undefined : value;
            })
            .required();
        }
        return y
          .string()
          .transform((value, originalValue) => {
            return originalValue === '' ? undefined : value;
          })
          .optional();
      }),
  }),
  downPaymentPercent: y.number().optional(),
  mortgageType: y.string().required(),
  loanAmortizationType: y.string().required(),
  fieldLockData: y.array(y.string()).optional(),
  loanAmortizationTermMonths: y
    .number()
    .optional()
    .when('loanAmortizationType', ([loanAmortizationType]) => {
      if (loanAmortizationType !== 'AdjustableRate') {
        return y.number().positive().required();
      }
      return y.number().optional();
    }),
  applications: y.array(CoborrowerApplicationContract),
  property: PropertyContract,
  customFields: y.array(FieldContract),
  loanProductData: LoanProductDataContract,
  prequalification: PrequalificationContract.optional(),
});

export const StoreLoanContract = LoanContract.omit([
  'applications',
  'customFields',
]);

export const ValidationLoanContract = LoanContract.shape({
  currentApplication: ApplicationContract,
  customFields: ValidationFieldContract,
});

export interface Loan extends y.Asserts<typeof LoanContract> {}
export interface StoreLoan extends y.Asserts<typeof StoreLoanContract> {}
export interface ValidationLoan
  extends y.Asserts<typeof ValidationLoanContract> {}

export const loanNewValidationErrorMap = {
  _entityName: 'Loan',
  loan: {
    type: {
      mortgageType: typeValidationKeys.loan.mortgageType,
    },
    'loan-amount': {
      borrowerRequestedLoanAmount:
        typeValidationKeys.loan.borrowerRequestedLoanAmount,
    },
    'borrow-amount': {
      downPayment: {
        amount: typeValidationKeys.loan.downPaymentAmount,
        sourceDescription: typeValidationKeys.loan.downPaymentSourceDescription,
      },
      downPaymentPercent: typeValidationKeys.loan.downPaymentPercent,
      purchasePriceAmount: typeValidationKeys.loan.purchasePriceAmount,
    },
    terms: {
      loanAmortizationType: typeValidationKeys.loan.loanAmortizationType,
    },
    'years-to-pay': {
      loanAmortizationTermMonths:
        typeValidationKeys.loan.loanAmortizationTermMonths,
    },
    'property-usage': {
      currentApplication: {
        propertyUsageType: typeValidationKeys.loan.propertyUsageType,
      },
    },
    'target-market': {
      idealPurchasePrice: typeValidationKeys.loan.purchasePriceAmount,
    },
    'rate-adjustment': {
      loanProductData: {
        fnmProductPlanIdentifier:
          typeValidationKeys.loan.fnmProductPlanIdentifier,
      },
    },
  },
};
